import React, { useEffect, useMemo } from 'react';
import styles from './WorkerWorkTime.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { getWorker, getWorkerWorkTimes } from '../../../../store/selectors';
import { salonManagementActions } from '../../../../store';
import { useNavigate, useParams } from 'react-router-dom';
import { getActiveSalonId } from '../../../../../Auth/store/selectors';
import TitleSectionBack from '../../../../../Common/TitleSectionBack';
import AvatarIcon from '../../../../../Common/AvatarIcon';
import { modalActions } from '../../../../../Modals/store';
import clx from 'classnames';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import useWindowSize from '../../../../../../utils/useWindowSize';
import { ROUTES } from '../../../../../../routes/constants';
import { useTranslation } from 'react-i18next';
import { CalendarOff, CalendarPlus, ChevronRight } from 'lucide-react';

dayjs.locale('it');

const formatSlot = (from, to) => {
  if (!from || !to) return null;
  return `${dayjs(from, 'HH:mm:ss').format('HH:mm')}–${dayjs(to, 'HH:mm:ss').format('HH:mm')}`;
};

const getDayScheduleLines = (timeValue) => {
  if (timeValue?.chiuso) return [];
  const morning = timeValue?.orariApertura?.mattina;
  const afternoon = timeValue?.orariApertura?.pomeriggio;
  if (timeValue?.isFullDay) {
    const line = formatSlot(morning?.dalle, afternoon?.alle);
    return line ? [line] : [];
  }
  return [formatSlot(morning?.dalle, morning?.alle), formatSlot(afternoon?.dalle, afternoon?.alle)].filter(
    Boolean,
  );
};

const WorkerWorkTime = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { workerId } = useParams();
  const navigate = useNavigate();
  const worker = useSelector(getWorker());
  const activeSalonId = useSelector(getActiveSalonId());
  const workTimes = useSelector(getWorkerWorkTimes());
  const { isMobile } = useWindowSize();
  const todayIndex = dayjs().day();

  useEffect(() => {
    if (workerId !== 'new') {
      dispatch(salonManagementActions.GET_WORKER.REQUEST(workerId, null, { skipLoader: true }));
      dispatch(salonManagementActions.GET_WORKER_WORK_TIMES.REQUEST(workerId, null, { skipLoader: true }));
    }
    return () => {
      dispatch(salonManagementActions.GET_WORKER.CLEAR());
    };
  }, [workerId]);

  useEffect(() => {
    if (worker?.salone && worker?.salone !== activeSalonId) {
      dispatch(salonManagementActions.GET_WORKER.CLEAR());
      navigate(-1);
    }
  }, [worker, activeSalonId]);

  const sortedWorkTimes = useMemo(() => {
    const list = Array.isArray(workTimes) ? [...workTimes] : [];
    return list.sort((a, b) => {
      const aKey = a.indexDay === 0 ? 7 : a.indexDay;
      const bKey = b.indexDay === 0 ? 7 : b.indexDay;
      return aKey - bKey;
    });
  }, [workTimes]);

  const openDayModal = (indexDay) => {
    dispatch(
      modalActions.OPEN_MODAL.SUCCESS({
        type: 'editWorkerWorkTimeModalOneDay',
        props: { indexDay },
      }),
    );
  };

  return (
    <div className={clx(styles.page, { [styles.page_mobile]: isMobile })}>
      <div className={styles.top_bar}>
        <TitleSectionBack
          title={t('workers.workerWorkTime.title')}
          description={t('workers.workerWorkTime.description')}
        />
        <div className={styles.worker_chip}>
          <AvatarIcon name={worker?.nome_referente} icon={worker?.icon} size="40px" />
          <div className={styles.worker_chip_copy}>
            <span>{t('workers.workerWorkTime.modifyingSchedule')}</span>
            <strong>{worker?.nome_referente || '—'}</strong>
          </div>
        </div>
      </div>

      <div className={styles.week_panel}>
        <div className={styles.week_panel_head}>
          <h3>{t('workers.workerWorkTime.weekTitle')}</h3>
          <p>{t('workers.workerWorkTime.weekHint')}</p>
        </div>

        <div className={styles.week_grid}>
          {sortedWorkTimes.map((timeValue) => {
            const isToday = todayIndex === timeValue.indexDay;
            const isClosed = Boolean(timeValue.chiuso);
            const lines = getDayScheduleLines(timeValue);
            const dayShort = dayjs().day(timeValue.indexDay).format('ddd');
            const dayFull = dayjs().day(timeValue.indexDay).format('dddd');

            return (
              <button
                key={timeValue.indexDay}
                type="button"
                className={clx(styles.day_card, {
                  [styles.day_card_today]: isToday,
                  [styles.day_card_closed]: isClosed,
                  [styles.day_card_open]: !isClosed,
                })}
                onClick={() => openDayModal(timeValue.indexDay)}
              >
                <div className={styles.day_card_top}>
                  <span className={styles.day_short}>{dayShort}</span>
                  <span className={styles.day_full}>{dayFull}</span>
                  {isToday ? <span className={styles.today_dot}>{t('workers.workerWorkTime.today')}</span> : null}
                </div>

                <div className={styles.day_card_body}>
                  {isClosed ? (
                    <span className={styles.closed_label}>{t('workers.workerWorkTime.closed')}</span>
                  ) : (
                    lines.map((line) => (
                      <span key={line} className={styles.hours}>
                        {line}
                      </span>
                    ))
                  )}
                </div>

                <span className={styles.day_card_footer}>
                  {t('workers.workerWorkTime.editSchedule')}
                  <ChevronRight size={16} strokeWidth={2.4} aria-hidden />
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.extras_grid}>
        <button
          type="button"
          className={styles.extra_card}
          onClick={() =>
            navigate(
              `${ROUTES.SALON_MANAGEMENT}${ROUTES.WORKERS}${ROUTES.CLOSURES}/${worker.idpoltrona}`,
            )
          }
        >
          <span className={clx(styles.extra_icon, styles.extra_icon_off)} aria-hidden>
            <CalendarOff size={18} strokeWidth={2.2} />
          </span>
          <span className={styles.extra_copy}>
            <strong>{t('workers.workerWorkTime.extraClosure')}</strong>
            <span>{t('workers.workerWorkTime.extraClosureDescription')}</span>
          </span>
          <ChevronRight size={18} strokeWidth={2.3} className={styles.extra_chevron} aria-hidden />
        </button>

        <button
          type="button"
          className={styles.extra_card}
          onClick={() =>
            navigate(
              `${ROUTES.SALON_MANAGEMENT}${ROUTES.WORKERS}${ROUTES.EXTRA_WORKING_TIME}/${worker.idpoltrona}`,
            )
          }
        >
          <span className={clx(styles.extra_icon, styles.extra_icon_on)} aria-hidden>
            <CalendarPlus size={18} strokeWidth={2.2} />
          </span>
          <span className={styles.extra_copy}>
            <strong>{t('workers.workerWorkTime.extraOpening')}</strong>
            <span>{t('workers.workerWorkTime.extraOpeningDescription')}</span>
          </span>
          <ChevronRight size={18} strokeWidth={2.3} className={styles.extra_chevron} aria-hidden />
        </button>
      </div>
    </div>
  );
};

export default WorkerWorkTime;
